import { apiFetch } from '../client';
import type {
    ApplicationWire,
    ApplicationPermissionWire,
    ApplicationListItemLocal,
    ApplicationDetailLocal,
    ApplicationPermissionLocal,
    CreateApplicationRequestLocal,
    UpdateApplicationRequestLocal,
    UpsertApplicationPermissionRequestLocal,
    UpdateApplicationPermissionResponseWire,
    UpdateApplicationPermissionResultLocal,
} from '../types/ApplicationLocal';
import {
    normalizeApplicationList,
    normalizeApplicationDetail,
    normalizeApplicationPermissionList,
    normalizeApplicationPermission,
} from '../types/applicationNormalizers';

interface RequestOpts {
    idempotencyKey?: string;
}

// Helper local duplicado por consistencia del repo (igual que tenant.ts/retail.ts/platform.ts).
function withIdempotency(opts: RequestOpts): RequestInit {
    return opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {};
}

/**
 * Client tipado del modelo "Aplicación" (plantilla) y su catálogo de permisos. Endpoints/verbos
 * verificados en `backoffice.gen.ts`:
 *   GET/POST  `/applications`                                  (list / create)
 *   GET/PUT   `/applications/{applicationId}`                  (detail / update)
 *   GET/POST  `/applications/{applicationId}/permissions`      (list / create permiso)
 *   PUT/DELETE `/applications/{applicationId}/permissions/{permissionKey}` (update / delete permiso)
 *
 * Gateados server-side por `platform.application.manage` (scope PLATFORM). `apiFetch` ya desenvuelve
 * `data`; los normalizers transforman epoch ms → ISO. NO se hand-rollean shapes (TD-WEBAPP-001).
 */
export const applicationApi = {
    // ─── Aplicaciones ───
    list: (): Promise<{ items: ApplicationListItemLocal[] }> =>
        apiFetch<{ items?: ApplicationWire[] }>('/applications').then(normalizeApplicationList),

    create: (
        body: CreateApplicationRequestLocal,
        opts: RequestOpts = {},
    ): Promise<ApplicationDetailLocal> =>
        apiFetch<ApplicationWire>('/applications', {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }).then(normalizeApplicationDetail),

    get: (applicationId: string): Promise<ApplicationDetailLocal> =>
        apiFetch<ApplicationWire>(`/applications/${applicationId}`).then(
            normalizeApplicationDetail,
        ),

    update: (
        applicationId: string,
        body: UpdateApplicationRequestLocal,
    ): Promise<ApplicationDetailLocal> =>
        apiFetch<ApplicationWire>(`/applications/${applicationId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizeApplicationDetail),

    // DEC-RBAC-088: hard delete con guarda server-side. DELETE sin body; éxito → 200 (Ok).
    // Si la app tiene tenants alineados el backend responde 409 `APPLICATION_HAS_TENANTS`
    // (con `data: { applicationId, tenants, tenantIds }`), y 404 `APPLICATION_NOT_FOUND` si no existe;
    // ambos los propaga `apiFetch` como `ApiError` para que la UI mapee el mensaje. No requiere tipo extra.
    delete: (applicationId: string) =>
        apiFetch(`/applications/${applicationId}`, { method: 'DELETE' }),

    // ─── Catálogo de permisos de una aplicación ───
    listPermissions: (applicationId: string): Promise<{ items: ApplicationPermissionLocal[] }> =>
        apiFetch<{ items?: ApplicationPermissionWire[] }>(
            `/applications/${applicationId}/permissions`,
        ).then(normalizeApplicationPermissionList),

    createPermission: (
        applicationId: string,
        body: UpsertApplicationPermissionRequestLocal,
        opts: RequestOpts = {},
    ): Promise<ApplicationPermissionLocal> =>
        apiFetch<ApplicationPermissionWire>(`/applications/${applicationId}/permissions`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }).then(normalizeApplicationPermission),

    updatePermission: (
        applicationId: string,
        permissionKey: string,
        body: UpsertApplicationPermissionRequestLocal,
    ): Promise<UpdateApplicationPermissionResultLocal> =>
        apiFetch<UpdateApplicationPermissionResponseWire>(
            `/applications/${applicationId}/permissions/${permissionKey}`,
            {
                method: 'PUT',
                body: JSON.stringify(body),
            },
        ).then((res) => ({
            permission: normalizeApplicationPermission(res.permission),
            // DEC-RBAC-092: si el update cambió el scope, el backend adjunta el resumen del cascade.
            cascade: res.cascade,
        })),

    // DELETE sin body: el backend responde 200 `{ deleted: true }`. No requiere tipo generado extra.
    deletePermission: (applicationId: string, permissionKey: string) =>
        apiFetch<{ deleted?: boolean }>(
            `/applications/${applicationId}/permissions/${permissionKey}`,
            { method: 'DELETE' },
        ),
};
